import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { usePremisesStore } from "../../../store/store";
import { PageLoader } from "../../../components/loader/Loader";
import { themeQuartz } from "ag-grid-community";
import Modal from "../../../components/modal/Modal";
import images from "../../../assets/images";
import axiosInstance from "../../../common/axiosInstance";
import { toast } from "react-toastify";
import { useAuth } from "../../../auth/AuthProvider";
import { useOrgUsersStore } from "../../../store/store";

interface Checklist {
  _id: string;
  name: string;
  premisesId: string;
  materialType?: string;
}

interface Premise {
  id: string;
  premisesName: string;
}

interface Inspection {
  _id: string;
  checklistId: string;
  premisesId: string;
  inspectionDate: string;
  status: "scheduled" | "pending" | "completed";
  assignedTo: string;
  remarks?: string;
  checklist?: Checklist;
  premises?: Premise;
}

interface InspectionFormData {
  premiseId: string;
  checklistId: string;
  inspectionDate: string;
  assignedTo: string;
}

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const HazMatInspectionList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentInspectionId, setCurrentInspectionId] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<InspectionFormData>({
    premiseId: "",
    checklistId: "",
    inspectionDate: new Date().toISOString().split("T")[0],
    assignedTo: "",
  });
  const [formErrors, setFormErrors] = useState<Partial<InspectionFormData>>({});

  useEffect(() => {
    fetchPremises();
    fetchOrgUsers();
    fetchInspections();
    fetchChecklists();
  }, []);

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/hazmat-inspections`);

      if (response.status === 200) {
        setInspections(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching inspections:", error);
      toast.error("Failed to fetch inspections");
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklists = async () => {
    try {
      const response = await axiosInstance.get(`/api/hazmat-checklist-config`);

      if (response.status === 200) {
        setChecklists(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching checklists:", error);
    }
  };

  const filteredChecklists = useMemo(() => {
    if (!formData.premiseId) return [];
    return checklists.filter((c) => c.premisesId === formData.premiseId);
  }, [checklists, formData.premiseId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const resetForm = () => {
    setFormData({
      premiseId: "",
      checklistId: "",
      inspectionDate: new Date().toISOString().split("T")[0],
      assignedTo: "",
    });
    setFormErrors({});
    setIsEditMode(false);
    setCurrentInspectionId(null);
  };

  const validateForm = (): boolean => {
    const errors: Partial<InspectionFormData> = {};
    if (!formData.premiseId) errors.premiseId = "Premises is required";
    if (!formData.checklistId) errors.checklistId = "Checklist is required";
    if (!formData.inspectionDate)
      errors.inspectionDate = "Inspection date is required";
    if (!formData.assignedTo) errors.assignedTo = "Assigned to is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const url = isEditMode ? `/api/hazmat-inspections/${currentInspectionId}` : `/api/hazmat-inspections`;

      const response = await axiosInstance({
        method: isEditMode ? "put" : "post",
        url,
        data: {
          premisesId: formData.premiseId,
          checklistId: formData.checklistId,
          inspectionDate: formData.inspectionDate,
          assignedTo: formData.assignedTo,
          status: "scheduled",
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success(
          `Inspection ${isEditMode ? "updated" : "created"} successfully`
        );
        setModalOpen(false);
        resetForm();
        fetchInspections();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to save inspection");
    }
  };

  const handleEdit = (inspection: Inspection) => {
    setFormData({
      premiseId: inspection.premisesId,
      checklistId: inspection.checklistId,
      inspectionDate: inspection.inspectionDate.split("T")[0],
      assignedTo: inspection.assignedTo,
    });
    setCurrentInspectionId(inspection._id);
    setIsEditMode(true);
    setModalOpen(true);
  };

  const columnDefs = useMemo<ColDef<Inspection>[]>(
    () => [
      {
        field: "checklist.name",
        headerName: "Checklist Name",
        flex: 1,
        valueGetter: (params) => params.data?.checklist?.name || "N/A",
      },
      {
        field: "premises.premisesName",
        headerName: "Premises",
        flex: 1,
        valueGetter: (params) => {
          const premise = allPremises.find(
            (p) => p.id === params.data?.premisesId
          );
          return premise?.premisesName || "N/A";
        },
      },
      {
        field: "inspectionDate",
        headerName: "Inspection Date",
        flex: 1,
        valueGetter: (params) =>
          params.data?.inspectionDate
            ? new Date(params.data.inspectionDate).toLocaleDateString()
            : "N/A",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.5,
        cellRenderer: (params: any) => {
          const status = params.value;
          return (
            <div
              className={`px-3 py-1 rounded-full text-xs font-semibold ${status === "completed"
                  ? "bg-green-100 text-green-800"
                  : status === "scheduled"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
            >
              {status}
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        flex: 0.5,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-1">
            <button
              className="p-1 hover:bg-red-100 rounded"
              onClick={() =>
                navigate(
                  `/hazardous-materials/inspections/view/${params.data._id}`
                )
              }
            >
              <img
                className="iconTd w-5 h-5"
                src={images.viewIcon || "/placeholder.svg"}
                alt="View"
              />
            </button>
            {params.data.status !== "completed" && (
              <button
                className="p-1 hover:bg-green-100 rounded"
                onClick={() => handleEdit(params.data)}
              >
                <img
                  className="iconTd w-5 h-5"
                  src={images.editIcon || "/placeholder.svg"}
                  alt="Edit"
                />
              </button>
            )}
          </div>
        ),
      },
    ],
    [navigate, allPremises]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      flex: 1,
    }),
    []
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen mt-14 ml-2 min-h-100vh">
        <PageLoader />
      </div>
    );
  }

  return (
    <>
      <div className="px-2 py-4 mt-14 ml-2 min-h-100vh">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl ">Hazardous Material Inspections</h1>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => setModalOpen(true)}
          >
            CREATE INSPECTION
          </button>
        </div>

        <div className="ag-theme-alpine w-full h-[calc(100vh-200px)]">
          <AgGridReact
            rowData={inspections}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            theme={gridTheme}
            headerHeight={65}
            rowHeight={50}
            pagination={true}
            paginationPageSize={20}
            enableCellTextSelection={true}
            suppressCellFocus={true}
            domLayout="autoHeight"
          />
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
          resetForm();
        }}
        closeButton={false}
        modalClass={"bg-white w-full max-w-4xl rounded-xl shadow-lg relative"}
      >
        <div className="pt-6">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            {isEditMode ? "Update Inspection" : "Create New Inspection"}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 px-6 pb-6">
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Premises
                </label>
                <select
                  name="premiseId"
                  value={formData.premiseId}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${formErrors.premiseId ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                >
                  <option value="">Select Premises</option>
                  {allPremises.length > 0 ? (
                    allPremises.map((premise) => (
                      <option key={premise.id} value={premise.id}>
                        {premise.premisesName}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No premises available
                    </option>
                  )}
                </select>
                {formErrors.premiseId && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.premiseId}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Checklist Name
                </label>
                <select
                  name="checklistId"
                  value={formData.checklistId}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${formErrors.checklistId
                      ? "border-red-500"
                      : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                  disabled={!formData.premiseId}
                >
                  <option value="">
                    {!formData.premiseId
                      ? "Select Premises"
                      : "Select Checklist Name"}
                  </option>
                  {filteredChecklists.length > 0 ? (
                    filteredChecklists.map((checklist) => (
                      <option key={checklist._id} value={checklist._id}>
                        {checklist.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No checklists available for selected premises
                    </option>
                  )}
                </select>
                {formErrors.checklistId && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.checklistId}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Inspection Date
                </label>
                <input
                  type="date"
                  name="inspectionDate"
                  value={formData.inspectionDate}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${formErrors.inspectionDate
                      ? "border-red-500"
                      : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                />
                {formErrors.inspectionDate && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.inspectionDate}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned To
                </label>
                <select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${formErrors.assignedTo ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                >
                  <option value="">Select User</option>
                  {orgUsersData.map((user: any) => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {formErrors.assignedTo && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.assignedTo}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-4 px-6 pb-6">
              <button
                type="button"
                onClick={() => {
                  setModalOpen(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#0fa9a6] text-white rounded-md hover:bg-teal-600"
              >
                {isEditMode ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default HazMatInspectionList;
